// Returns the explicit `languageSW` URL override, or null when the param is
// absent. Null (rather than 0) is important: it lets the authenticated account
// language set via SET_USER survive — otherwise setUpMetadataByUserType would
// reset window.LANGUAGE_SW to 0 (German) on every load, ignoring the account.
const getLanguageSWFromQuery = (): number | null => {
  try {
    const { search, hash } = window.location;
    const hashQueryIndex = hash.indexOf('?');
    const hashQuery = hashQueryIndex !== -1 ? hash.substring(hashQueryIndex + 1) : '';
    const params = new URLSearchParams(search || hashQuery);
    const value = params.get('languageSW');
    if (value === null) return null;

    const parsed = parseInt(value, 10);

    return Number.isNaN(parsed) ? null : parsed;
  } catch (e) {
    return null;
  }
};

const languageSWFromQuery = getLanguageSWFromQuery();

let language = 0;
if (process.env.REACT_APP_LANGUAGE) {
  language = parseInt(process.env.REACT_APP_LANGUAGE, 10);
}

window.LANGUAGE_SW = languageSWFromQuery ?? 0;
window.LANGUAGE = language;
window.CDN_URL = '';
window.API_URL_DL = '';
window.API_URL = '';
window.IS_TEST = '';

const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT;

const API_URL = process.env.REACT_APP_API_URL;
const API_URL_DL = process.env.REACT_APP_API_URL_DL;
const API_PICTUREPEOPLE_URL = process.env.REACT_APP_API_PICTUREPEOPLE_URL;
const API_PICTUREPEOPLE_URL_DL = process.env.REACT_APP_API_PICTUREPEOPLE_URL;
const API_GALLERYPIX_URL = process.env.REACT_APP_API_GALLERYPIX_URL;
const API_GALLERYPIX_URL_DL = process.env.REACT_APP_API_GALLERYPIX_URL;

const CDN_URL = process.env.REACT_APP_CDN_URL;
const GALLERYPIX_CDN_URL = process.env.REACT_APP_GALLERYPIX_CDN_URL;
const PICTUREPEOPLE_CDN_URL = process.env.REACT_APP_PICTUREPEOPLE_CDN_URL;

const devEnvironments = ['development', 'staging'];
window.IS_TEST = devEnvironments.includes(ENVIRONMENT || '');

const { hostname } = window.location;

window.setUpMetadataByUserType = (userType?: string): void => {
  let iconUrl = '';

  if (userType === 'VST' || hostname.indexOf('gallerypix') !== -1) {
    const titleElement = document.getElementById('index_title') as HTMLTitleElement | null;
    if (titleElement) titleElement.text = 'VST - GalleryPix';

    const descElement = document.getElementById('index_description') as HTMLMetaElement | null;
    if (descElement) {
      descElement.content =
        'Fasziniere deine Kunden mit einer personalisierten mobilen Web App und hinterlasse damit einen bleibenden Eindruck…';
    }

    window.USER_TYPE = 'VST';

    window.CDN_URL = GALLERYPIX_CDN_URL || '';
    window.API_URL = API_GALLERYPIX_URL || '';
    window.API_URL_DL = API_GALLERYPIX_URL_DL || '';

    iconUrl = `${process.env.REACT_APP_PUBLIC_BUCKET_URL}/builder/logos/VST-Logo.png`;
  } else if (userType === 'PP' || hostname.indexOf('picturepeople') !== -1) {
    const titleElement = document.getElementById('index_title') as HTMLTitleElement | null;
    if (titleElement) titleElement.text = 'PicturePeople';

    const descElement = document.getElementById('index_description') as HTMLMetaElement | null;
    if (descElement) {
      descElement.content =
        'Fasziniere deine Kunden mit einer personalisierten mobilen Web App und hinterlasse damit einen bleibenden Eindruck…';
    }

    window.USER_TYPE = 'PP';

    window.CDN_URL = PICTUREPEOPLE_CDN_URL || '';
    window.API_URL = window.IS_TEST
      ? API_PICTUREPEOPLE_URL || ''
      : `https://api${hostname.substring(hostname.indexOf('.'))}`;
    window.API_URL_DL = API_PICTUREPEOPLE_URL_DL || '';

    iconUrl = `${process.env.REACT_APP_PUBLIC_BUCKET_URL}/builder/logos/favicon.ico`;
  } else {
    const titleElement = document.getElementById('index_title') as HTMLTitleElement | null;
    if (titleElement) {
      titleElement.text = process.env.REACT_APP_SITE_TITLE || 'scrappbook Builder';
    }

    const descElement = document.getElementById('index_description') as HTMLMetaElement | null;
    if (descElement) {
      descElement.content =
        process.env.REACT_APP_SITE_DESCRIPTION ||
        'Fasziniere deine Kunden mit einer personalisierten mobilen Web App und hinterlasse damit einen bleibenden Eindruck…';
    }

    window.USER_TYPE = 'SB';
    window.CDN_URL = CDN_URL || '';
    window.API_URL = API_URL || '';
    window.API_URL_DL = API_URL_DL || API_URL || '';
    iconUrl =
      process.env.REACT_APP_SB_ICON_URL ||
      'https://public-bucket.intercdn.de/sb-logos/sb-icon-512x512.png';
  }

  // Only an explicit ?languageSW= override is re-applied here; without it we must
  // NOT touch window.LANGUAGE_SW, or the account language (set by SET_USER right
  // before this call) would be clobbered back to German on every load.
  if (languageSWFromQuery !== null) {
    window.LANGUAGE_SW = languageSWFromQuery;
  }

  let link = document.querySelector("meta[property='og:image']") as HTMLMetaElement | null;
  if (!link) {
    link = document.createElement('meta');
    link.setAttribute('property', 'og:image');
    link.content = iconUrl;
    document.getElementsByTagName('head')[0].appendChild(link);
  } else {
    link.content = iconUrl;
  }

  let linkElement = document.getElementById('link-shortcut-icon') as HTMLLinkElement | null;
  if (!linkElement) {
    linkElement = document.createElement('link');
    linkElement.id = 'link-shortcut-icon';
    linkElement.rel = 'shortcut icon';
    linkElement.href = iconUrl;
    document.head.appendChild(linkElement);
  } else {
    linkElement.href = iconUrl;
  }

  linkElement = document.getElementById('link-icon') as HTMLLinkElement | null;
  if (!linkElement) {
    linkElement = document.createElement('link');
    linkElement.id = 'link-icon';
    linkElement.rel = 'icon';
    linkElement.href = iconUrl;
    document.head.appendChild(linkElement);
  } else {
    linkElement.href = iconUrl;
  }

  linkElement = document.getElementById('link-apple-touch-icon') as HTMLLinkElement | null;
  if (!linkElement) {
    linkElement = document.createElement('link');
    linkElement.id = 'link-apple-touch-icon';
    linkElement.rel = 'apple-touch-icon';
    linkElement.href = iconUrl;
    document.head.appendChild(linkElement);
  } else {
    linkElement.href = iconUrl;
  }
};

window.setUpMetadataByUserType(undefined);

export {};
